import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { GetOneParams, GetOneResult, UpdateParams, UpdateResult, DeleteParams, DeleteResult } from "react-admin";
import { z } from "zod";
import { DateTime } from "luxon";
import { diff as deepObjectDiff } from "deep-object-diff";
import { firestore } from "../firebase";
import { CourseLesson } from "../model/autovioCalendarEvents";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { updateApplied } from "../utils/updateApplied";
import { courseLessonConverter } from "./converter/courseLessonConverter";
import { splitCompositeId } from "../backoffice.utils";

const EditCourseLessonDtoSchema = schemas.EditCalendarEventDto3.options.find(
  (it) => it.shape.type._def.value === "CourseLesson",
)!;
type EditCourseLessonDto = z.infer<typeof EditCourseLessonDtoSchema>;

class CourseLessonsProvider extends AbstractDataProvider<CourseLesson> {
  async getOne(_: string, { id }: GetOneParams): Promise<GetOneResult<CourseLesson>> {
    const snapshot = await getDoc(doc(firestore, `calendar_events/${id}`));
    const courseLesson = courseLessonConverter.fromFirestore(snapshot);
    return { data: courseLesson };
  }

  async update(_: string, { id, data, previousData }: UpdateParams<CourseLesson>): Promise<UpdateResult<CourseLesson>> {
    const diff = deepObjectDiff(previousData, data) as Partial<CourseLesson>;
    const updateDto: EditCourseLessonDto = { type: "CourseLesson" };
    const updateData: Partial<CourseLesson> = {};
    const unsupportedKeys: Array<string> = [];
    for (const key of Object.keys(diff)) {
      if (key in EditCourseLessonDtoSchema.shape) {
        const value = data[key];
        if (key === "start") {
          if (!DateTime.isDateTime(value) || !value.isValid) {
            throw new Error(`Invalid data.start: ${value} -- expected a valid DateTime instance`);
          }
          const start = value;
          const duration = previousData.end.diff(previousData.start);
          const end = start.plus(duration);
          Object.assign(updateDto, { start: start.toISO(), end: end.toISO() });
          Object.assign(updateData, { start, end });
        } else if (key === "lecturerId") {
          updateDto[key] = value ? value : null;
          updateData[key] = value ? value : null;
        } else if (key === "roomId") {
          updateDto[key] = value ? splitCompositeId(value)[1] : null;
          updateData[key] = value ? value : null;
        } else {
          updateDto[key as keyof EditCourseLessonDto] = value;
          updateData[key] = value;
        }
      } else {
        unsupportedKeys.push(key);
      }
    }
    if (unsupportedKeys.length === 1) {
      throw new Error(`Changing property ${unsupportedKeys[0]} is not supported.`);
    } else if (unsupportedKeys.length > 1) {
      throw new Error(`Changing properties ${unsupportedKeys.join(", ")} is not supported.`);
    }
    let courseLesson = previousData;
    if (Object.keys(updateData).length > 0) {
      const serverClient = await getAuthenticatedServerClient();
      const updateReceived = new Promise<void>((resolve) => {
        const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${id}`), (snapshot) => {
          courseLesson = courseLessonConverter.fromFirestore(snapshot);
          if (updateApplied(updateData, courseLesson)) {
            unsubscribe();
            resolve();
          }
        });
      });
      await serverClient.editEvent_v3(updateDto, { params: { eventId: id } });
      await updateReceived;
    }
    return { data: courseLesson };
  }

  async delete(_: string, { id }: DeleteParams): Promise<DeleteResult> {
    const updateReceived = new Promise<void>((resolve) => {
      const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${id}`), (snapshot) => {
        const updatedCourseLesson = courseLessonConverter.fromFirestore(snapshot);
        if (updateApplied({ deleted: true }, updatedCourseLesson)) {
          unsubscribe();
          resolve();
        }
      });
    });
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteCalendarEvent(undefined, { params: { eventId: id } });
    await updateReceived;
    return { data: {} };
  }
}

export const courseLessonsProvider = new CourseLessonsProvider();
